import React, { lazy } from "react";
import { createRoutesFromElements, Route, Navigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout";
import ProtectedRoute from "../components/layout-components/ProtectedRoute";
import AuthLayout from "../layouts/AuthLayout";

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// Each page is split into its own JS chunk that the browser loads on demand
// and then caches. This prevents all pages being parsed upfront in one bundle.
// ---------------------------------------------------------------------------
const AdminDashboard        = lazy(() => import("../pages/AdminDashboard"));
const SuperAdminDashboard   = lazy(() => import("../pages/SuperAdminDashboard"));
const CareTakerDashboard    = lazy(() => import("../pages/CareTakerDashboard"));
const NotFound              = lazy(() => import("../pages/NotFound"));
const Login                 = lazy(() => import("../pages/auth-pages/Login"));
const ResetPassword         = lazy(() => import("../pages/auth-pages/ResetPassword"));
const EmailConfirmationResult = lazy(() => import("../pages/auth-pages/EmailConfirmationResult"));
const InstitutionsPage      = lazy(() => import("../pages/InstitutionsPage"));
const RoomsPage             = lazy(() => import("../pages/RoomsPage"));
const RolesPage             = lazy(() => import("../pages/RolesPage"));
const InfoPage              = lazy(() => import("../pages/InfoPage"));
const UsersPage             = lazy(() => import("../pages/UsersPage"));
const UserList              = lazy(() => import("../pages/UserList"));
const GatewayPage           = lazy(() => import("../pages/GatewayPage"));
const GatewayAdminPage      = lazy(() => import("../pages/GatewayAdminPage"));
const SensorPage            = lazy(() => import("../pages/SensorPage"));
const SensorAdminPage       = lazy(() => import("../pages/SensorAdminPage"));
const HistoryPage           = lazy(() => import("../pages/HistoryPage"));
const MyPatientsPage        = lazy(() => import("../pages/MyPatientsPage"));
const AlarmSettingsPage     = lazy(() => import("../pages/AlarmSettingsPage"));
const CaregiverPage         = lazy(() => import("../pages/CaregiverPage"));
const MedicationPage        = lazy(() => import("../pages/MedicationPage"));
const SecurityHistory       = lazy(() => import("../pages/SecurityHistory"));
const ProfilePage           = lazy(() => import("../pages/ProfilePage"));
const LogSheet              = lazy(() => import("../pages/LogSheet"));
const SpecialRoleMyPatientsPage = lazy(() => import("../pages/SpecialRoleMyPatientsPage"));
const TermsPage             = lazy(() => import("../pages/TermsPage"));

// Define role-based access
const roleAccess = {
  superAdmin: ["1"],
  admin: ["2"],
  caregiver: ["3"],
  careGiverPatients: ["4"],
  security: ["5"],
  laundry: ["6"],
  food: ["7"],
  adminAndSuperAdmin: ["1", "2"],
  management: ["2", "3", "4"],
};

// Grouped route definitions for better organization
const superAdminRoutes = [
  { path: "superAdmin", element: SuperAdminDashboard },
  { path: "institutions", element: InstitutionsPage },
  { path: "roles", element: RolesPage },
];

const adminAndSuperAdminRoutes = [
  { path: "rooms", element: RoomsPage },
  { path: "users", element: UsersPage, children: [
    { index: true, element: () => <Navigate to="department" replace /> },
    { path: ":user_type", element: UserList }
  ]},
  { path: "gateway", element: GatewayPage },
  { path: "sensor", element: SensorPage },
  //{ path: "history", element: HistoryPage },
  { path: "alarm-settings", element: AlarmSettingsPage },
];

const adminRoutes = [
  { path: "gateway-admin", element: GatewayAdminPage },
  { path: "sensor-admin", element: SensorAdminPage },
];

const caregiverRoutes = [
  { path: "caregiver", element: CaregiverPage },
  { path: "historypage", element: HistoryPage},
];

// Updated special role routes to use SpecialRoleMyPatientsPage
const specialRoleRoutes = [
  // Security role routes
  { path: "security-patients", roles: roleAccess.security, element: SpecialRoleMyPatientsPage },
   { path: "security-history", roles: roleAccess.security, element: HistoryPage },
  // Food/F&B role routes  
  { path: "fnb-patients", roles: roleAccess.food, element: SpecialRoleMyPatientsPage },
  { path: "fnb-history", roles: roleAccess.food, element: HistoryPage },
  // Laundry/Housekeeping role routes
  { path: "housekeeping-patients", roles: roleAccess.laundry, element: SpecialRoleMyPatientsPage },
  { path: "housekeeping-history", roles: roleAccess.laundry, element: HistoryPage },
];

const otherRoleRoutes = [
  { path: "care-giver-patients", roles: roleAccess.careGiverPatients, element: MyPatientsPage },
];

const unrestrictedRoutes = [
  { path: "profile", element: ProfilePage, isProtected: true },
  { path: "info", element: InfoPage, isProtected: false },
  { path: "medication", element: MedicationPage, isProtected: true},
  { path: "history", element: HistoryPage, isProtected: true },
  { path: "not-found", element: NotFound, isProtected: false },
];

export const routes = createRoutesFromElements(
  <>
    {/* Authentication Routes */}
    <Route path="/auth" element={<AuthLayout />}>
      <Route path="login" element={<Login />} />
      <Route path="change-password" element={<ResetPassword />} />
      <Route path="email-confirmation" element={<EmailConfirmationResult />} />
    </Route>
    
    {/* Public Static Routes */}
    <Route path="/terms" element={<TermsPage />} />

    {/* Protected Routes under AppLayout */}
    <Route path="/" element={<AppLayout />}>
      {/* SuperAdmin Routes */}
      {superAdminRoutes.map(({ path, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roleAccess.superAdmin}><Element /></ProtectedRoute>}
        />
      ))}

      {/* Admin and SuperAdmin Shared Routes */}
      {adminAndSuperAdminRoutes.map(({ path, element: Element, children }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roleAccess.adminAndSuperAdmin}><Element /></ProtectedRoute>}
        >
          {children?.map(({ path: childPath, index, element: ChildElement }) => (
            <Route
              key={index ? `${path}-index` : childPath}
              index={index}
              path={index ? undefined : childPath}
              element={<ProtectedRoute roles={roleAccess.adminAndSuperAdmin}><ChildElement /></ProtectedRoute>}
            />
          ))}
        </Route>
      ))}

      {/* Admin Routes */}
      {adminRoutes.map(({ path, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roleAccess.admin}><Element /></ProtectedRoute>}
        />
      ))}

      {/* Caregiver Routes (Department) */}
      {caregiverRoutes.map(({ path, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roleAccess.caregiver}><Element /></ProtectedRoute>}
        />
      ))}

      {/* My Patients Route (Admin, Department, CareGiver) */}
      <Route
        path="my-patients"
        element={
          <ProtectedRoute roles={roleAccess.management}>
            <MyPatientsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Log Sheet Route (Department & Care Giver) */}
      <Route
        path="log-sheet"
        element={
          <ProtectedRoute roles={['3', '4','5','6','7']}>
            <LogSheet />
          </ProtectedRoute>
        }
      />

      {/* Special Role Routes - Updated to use SpecialRoleMyPatientsPage */}
      {specialRoleRoutes.map(({ path, roles, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roles}><Element /></ProtectedRoute>}
        />
      ))}

      {/* Other Role-Specific Routes */}
      {otherRoleRoutes.map(({ path, roles, element: Element }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute roles={roles}><Element /></ProtectedRoute>}
        />
      ))}

      {/* Unrestricted Routes */}
      {unrestrictedRoutes.map(({ path, element: Element, isProtected }) => (
        <Route
          key={path}
          path={path}
          element={isProtected ? <ProtectedRoute><Element /></ProtectedRoute> : <Element />}
        />
      ))}
    </Route>

    {/* Catch-all Route */}
    <Route path="*" element={<NotFound />} />
  </>
);
